import { createSlice, type PayloadAction } from "@reduxjs/toolkit";

// Types
export interface Address {
  addressLine1: string;
  addressLine2: string;
  city: string;
  postcode: string;
  country: string;
}

export interface Contact {
  id: string;
  isPrimary: boolean;
  fullName: string;
  role: "owner" | "director" | "partner" | "";
  emailAddress: string;
  phoneNumber: string;
  address: Address;
}

export interface Document {
  id: string;
  name: string;
  type: string;
  issueDate: string;
  expiryDate: string;
  notes: string;
  fileUrl: string;
  fileName: string;
  uploadedAt: string;
}

export interface BusinessInfo {
  legalName: string;
  tradingName: string;
  registrationNumber: string;
  businessType: "sole_trader" | "partnership" | "limited" | "";
  natureOfBusiness: string;
  tenantType: "business" | "supplier" | "";
  dateOfIncorporation: string;
  phoneNumber: string;
  emailAddress: string;
  preferredDomain: string;
}

export interface BankUK {
  accountHolderName: string;
  sortCode: string;
  accountNumber: string;
}

export interface BankEU {
  iban: string;
  bicSwift: string;
}

export type ApplicationStatus =
  | "draft"
  | "pending_review"
  | "pending_information"
  | "approved"
  | "rejected"
  | "provisioning"
  | "provisioning_failed"
  | "active";

export type ActiveTab =
  | "business"
  | "contacts"
  | "bank"
  | "documents"
  | "drafts";

// Initial values
const initialAddress: Address = {
  addressLine1: "",
  addressLine2: "",
  city: "",
  postcode: "",
  country: "GB",
};

const initialBusiness: BusinessInfo = {
  legalName: "",
  tradingName: "",
  registrationNumber: "",
  businessType: "",
  natureOfBusiness: "",
  tenantType: "",
  dateOfIncorporation: "",
  phoneNumber: "",
  emailAddress: "",
  preferredDomain: "",
};

const initialBankUK: BankUK = {
  accountHolderName: "",
  sortCode: "",
  accountNumber: "",
};

const initialBankEU: BankEU = {
  iban: "",
  bicSwift: "",
};

// UUID generator with fallback for environments where crypto.randomUUID is not available
const generateUUID = (): string => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  // Fallback implementation
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === "x" ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

const createEmptyContact = (isPrimary: boolean = false): Contact => ({
  id: generateUUID(),
  isPrimary,
  fullName: "",
  role: "",
  emailAddress: "",
  phoneNumber: "",
  address: { ...initialAddress },
});

// Application detail shape (for modal pattern)
export interface ApplicationDetailData {
  applicationId: string;
  applicationStatus: string;
  business: BusinessInfo;
  registeredAddress: Address;
  contacts: Contact[];
  bankUK: BankUK;
  bankEU: BankEU;
  documents: Document[];
}

// Draft list item (lightweight, for list view)
export interface DraftListItemState {
  application_id: string;
  legal_name: string;
  tenant_type: "business" | "supplier";
  contact_name: string;
  completion_percentage: number;
  status: string;
  updated_at: string;
  created_at: string;
}

// State interface
export interface BusinessOnboardingState {
  // Application
  applicationId: string | null;
  applicationStatus: ApplicationStatus;

  // Form Data
  business: BusinessInfo;
  registeredAddress: Address;
  contacts: Contact[];
  bankUK: BankUK;
  bankEU: BankEU;
  documents: Document[];

  // Drafts List (SSR GET pattern)
  drafts: DraftListItemState[];
  draftsPagination: {
    page: number;
    totalPages: number;
    totalCount: number;
  };

  // Selected Application Detail (modal pattern)
  selectedApplication: ApplicationDetailData | null;
  selectedApplicationLoading: boolean;
  selectedApplicationError: string | null;

  // UI State
  activeTab: ActiveTab;
  isLoading: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  error: string | null;
  fieldErrors: Record<string, string[]> | null;

  // Document Modals
  documentUploadModalOpen: boolean;
  documentDetailModal: {
    open: boolean;
    document: Document | null;
  };
  documentPreviewModal: {
    open: boolean;
    document: Document | null;
  };
}

const initialState: BusinessOnboardingState = {
  // Application
  applicationId: null,
  applicationStatus: "draft",

  // Form Data
  business: initialBusiness,
  registeredAddress: initialAddress,
  contacts: [createEmptyContact(true)],
  bankUK: initialBankUK,
  bankEU: initialBankEU,
  documents: [],

  // Drafts List (SSR GET pattern)
  drafts: [],
  draftsPagination: {
    page: 1,
    totalPages: 1,
    totalCount: 0,
  },

  // Selected Application Detail (modal pattern)
  selectedApplication: null,
  selectedApplicationLoading: false,
  selectedApplicationError: null,

  // UI State
  activeTab: "business",
  isLoading: false,
  isSaving: false,
  isSubmitting: false,
  error: null,
  fieldErrors: null,

  // Document Modals
  documentUploadModalOpen: false,
  documentDetailModal: {
    open: false,
    document: null,
  },
  documentPreviewModal: {
    open: false,
    document: null,
  },
};

export const businessOnboardingSlice = createSlice({
  name: "businessOnboarding",
  initialState,
  reducers: {
    // Application
    setApplicationId: (state, action: PayloadAction<string | null>) => {
      state.applicationId = action.payload;
    },
    setApplicationStatus: (state, action: PayloadAction<ApplicationStatus>) => {
      state.applicationStatus = action.payload;
    },

    // Business
    setBusiness: (state, action: PayloadAction<BusinessInfo>) => {
      state.business = action.payload;
    },
    updateBusiness: (state, action: PayloadAction<Partial<BusinessInfo>>) => {
      state.business = { ...state.business, ...action.payload };
    },

    // Registered Address
    setRegisteredAddress: (state, action: PayloadAction<Address>) => {
      state.registeredAddress = action.payload;
    },
    updateRegisteredAddress: (
      state,
      action: PayloadAction<Partial<Address>>,
    ) => {
      state.registeredAddress = {
        ...state.registeredAddress,
        ...action.payload,
      };
    },

    // Contacts
    setContacts: (state, action: PayloadAction<Contact[]>) => {
      state.contacts = action.payload;
    },
    addContact: (state, action: PayloadAction<Contact>) => {
      state.contacts.push(action.payload);
    },
    updateContact: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Contact> }>,
    ) => {
      const index = state.contacts.findIndex((c) => c.id === action.payload.id);
      if (index !== -1) {
        state.contacts[index] = {
          ...state.contacts[index],
          ...action.payload.updates,
        };
      }
    },
    removeContact: (state, action: PayloadAction<string>) => {
      state.contacts = state.contacts.filter((c) => c.id !== action.payload);
    },

    // Bank UK
    setBankUK: (state, action: PayloadAction<BankUK>) => {
      state.bankUK = action.payload;
    },
    updateBankUK: (state, action: PayloadAction<Partial<BankUK>>) => {
      state.bankUK = { ...state.bankUK, ...action.payload };
    },

    // Bank EU
    setBankEU: (state, action: PayloadAction<BankEU>) => {
      state.bankEU = action.payload;
    },
    updateBankEU: (state, action: PayloadAction<Partial<BankEU>>) => {
      state.bankEU = { ...state.bankEU, ...action.payload };
    },

    // Drafts List (SSR GET pattern)
    setDrafts: (state, action: PayloadAction<DraftListItemState[]>) => {
      state.drafts = action.payload;
    },
    setDraftsPagination: (
      state,
      action: PayloadAction<{ page: number; totalPages: number; totalCount: number }>,
    ) => {
      state.draftsPagination = action.payload;
    },

    // Selected Application Detail (modal pattern)
    setSelectedApplication: (
      state,
      action: PayloadAction<ApplicationDetailData | null>,
    ) => {
      state.selectedApplication = action.payload;
    },
    setSelectedApplicationLoading: (state, action: PayloadAction<boolean>) => {
      state.selectedApplicationLoading = action.payload;
    },
    setSelectedApplicationError: (
      state,
      action: PayloadAction<string | null>,
    ) => {
      state.selectedApplicationError = action.payload;
    },

    // Documents
    setDocuments: (state, action: PayloadAction<Document[]>) => {
      state.documents = action.payload;
    },
    addDocument: (state, action: PayloadAction<Document>) => {
      state.documents.push(action.payload);
    },
    removeDocument: (state, action: PayloadAction<string>) => {
      state.documents = state.documents.filter((d) => d.id !== action.payload);
    },

    // UI State
    setActiveTab: (state, action: PayloadAction<ActiveTab>) => {
      state.activeTab = action.payload;
    },
    setIsLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setIsSaving: (state, action: PayloadAction<boolean>) => {
      state.isSaving = action.payload;
    },
    setIsSubmitting: (state, action: PayloadAction<boolean>) => {
      state.isSubmitting = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    setFieldErrors: (
      state,
      action: PayloadAction<Record<string, string[]> | null>,
    ) => {
      state.fieldErrors = action.payload;
    },

    // Document Upload Modal
    openDocumentUploadModal: (state) => {
      state.documentUploadModalOpen = true;
    },
    closeDocumentUploadModal: (state) => {
      state.documentUploadModalOpen = false;
    },

    // Document Detail Modal
    openDocumentDetailModal: (state, action: PayloadAction<Document>) => {
      state.documentDetailModal = {
        open: true,
        document: action.payload,
      };
    },
    closeDocumentDetailModal: (state) => {
      state.documentDetailModal = {
        open: false,
        document: null,
      };
    },

    // Document Preview Modal
    openDocumentPreviewModal: (state, action: PayloadAction<Document>) => {
      state.documentPreviewModal = {
        open: true,
        document: action.payload,
      };
    },
    closeDocumentPreviewModal: (state) => {
      state.documentPreviewModal = {
        open: false,
        document: null,
      };
    },

    // Reset form to initial state
    resetOnboardingForm: (state) => {
      state.applicationId = null;
      state.applicationStatus = "draft";
      state.business = initialBusiness;
      state.registeredAddress = initialAddress;
      state.contacts = [createEmptyContact(true)];
      state.bankUK = initialBankUK;
      state.bankEU = initialBankEU;
      state.documents = [];
      state.activeTab = "business";
      state.isLoading = false;
      state.isSaving = false;
      state.isSubmitting = false;
      state.error = null;
      state.fieldErrors = null;
    },

    // Hydrate state from API (for editing existing application)
    hydrateOnboardingState: (
      state,
      action: PayloadAction<Partial<BusinessOnboardingState>>,
    ) => {
      return { ...state, ...action.payload };
    },
  },
});

export const {
  // Application
  setApplicationId,
  setApplicationStatus,
  // Business
  setBusiness,
  updateBusiness,
  // Registered Address
  setRegisteredAddress,
  updateRegisteredAddress,
  // Contacts
  setContacts,
  addContact,
  updateContact,
  removeContact,
  // Bank UK
  setBankUK,
  updateBankUK,
  // Bank EU
  setBankEU,
  updateBankEU,
  // Drafts List (SSR GET pattern)
  setDrafts,
  setDraftsPagination,
  // Selected Application Detail (modal pattern)
  setSelectedApplication,
  setSelectedApplicationLoading,
  setSelectedApplicationError,
  // Documents
  setDocuments,
  addDocument,
  removeDocument,
  // UI State
  setActiveTab,
  setIsLoading,
  setIsSaving,
  setIsSubmitting,
  setError,
  setFieldErrors,
  // Document Upload Modal
  openDocumentUploadModal,
  closeDocumentUploadModal,
  // Document Detail Modal
  openDocumentDetailModal,
  closeDocumentDetailModal,
  // Document Preview Modal
  openDocumentPreviewModal,
  closeDocumentPreviewModal,
  // Reset & Hydrate
  resetOnboardingForm,
  hydrateOnboardingState,
} = businessOnboardingSlice.actions;

export default businessOnboardingSlice.reducer;
